import Check from '@mui/icons-material/Check';
import LoadingButton from '@mui/lab/LoadingButton';
import { Alert, Box, Link as MUILink, Paper, Typography } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { UserPlan } from '../../invoice-app/api/gql';
import { getConfig } from '../utils/config';

const { landingPageUrl, supportEmail } = getConfig();

const TRANS_NS = 'billing';

type Props = {
  isBatch?: boolean;
  userPlan: UserPlan;
  remindingsLeft: number;
  invoicesCount?: number;
  handleSubmit: () => void;
  isLoading: boolean;
  transNs?: string;
};

export const BillingBox = ({
  isBatch,
  userPlan,
  remindingsLeft,
  invoicesCount,
  handleSubmit,
  isLoading,
  transNs = TRANS_NS,
}: Props) => {
  const { t } = useTranslation(transNs);

  const isRemindingAvailable = remindingsLeft > 0 && (!invoicesCount || remindingsLeft >= invoicesCount);

  return (
    <Paper sx={{ py: 4, px: { md: 8, xs: 4 }, borderColor: 'primary.main', borderWidth: 2, textAlign: 'center' }}>
      {!isRemindingAvailable ? (
        <>
          <Typography variant={'h2'} color={'text.secondary'}>
            {isBatch ? t('remindingNotAvailable.batchHeading') : t('remindingNotAvailable.heading')}
          </Typography>
          <Typography color={'text.secondary'} mt={8}>
            {isBatch
              ? t('remindingNotAvailable.batchDescription', {
                  invoicesCount,
                  remindingsLeft,
                  invoice: t('remindingNotAvailable.invoice', { count: invoicesCount }),
                  invoiceAcc: t('remindingNotAvailable.invoiceAcc', { count: remindingsLeft }),
                })
              : t('remindingNotAvailable.description')}
          </Typography>
          <Typography color={'text.secondary'} mt={4}>
            <Trans
              t={t}
              i18nKey={'remindingNotAvailable.upgradePlan'}
              values={{ supportEmail }}
              components={{
                link1: (
                  <MUILink component={Link} to={`mailto:${supportEmail}`} target="_blank" rel="noopener noreferrer" />
                ),
              }}
            />
          </Typography>
          <Typography color={'text.secondary'} mt={4}>
            <Trans
              t={t}
              i18nKey="remindingNotAvailable.priceLink"
              values={{ landingPageUrl: landingPageUrl.replace(/^https?:\/\//, '') }}
              components={{
                link1: <MUILink component={Link} to={landingPageUrl} target="_blank" rel="noopener noreferrer" />,
              }}
            />
          </Typography>
        </>
      ) : (
        <>
          <Typography variant={'h2'}>
            {userPlan === UserPlan.Optimal ? t('optimal.heading') : t('complete.heading')}
          </Typography>

          <Box
            display={'flex'}
            flexDirection={'column'}
            alignItems={'center'}
            justifyContent={'center'}
            mt={{ md: 6, xs: 4 }}
          >
            {userPlan === UserPlan.Optimal && (
              <Alert severity={'info'}>{t('remindingsLeft', { count: remindingsLeft })}</Alert>
            )}
            <Typography whiteSpace={'break-spaces'} my={4} color={'text.secondary'}>
              {t('description')}
            </Typography>
            <LoadingButton
              startIcon={<Check />}
              loading={isLoading}
              variant={'contained'}
              onClick={handleSubmit}
              size={'large'}
            >
              {userPlan === UserPlan.Optimal ? t('optimal.submit') : t('complete.submit')}
            </LoadingButton>
          </Box>
        </>
      )}
    </Paper>
  );
};
